import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { Box, Typography, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'sonner';
import { usePolyAuth } from '../hooks/PolyAuthContext';
import { fallbackCopyTextToClipboard } from '../utils/utils';
import CircularProgress from '@mui/material/CircularProgress';
import FailuresSection from './meeting/FailuresSection';
import NotPolyguardMeeting from './meeting/NotPolyguardMeeting';
import WaitingForParticipants from './meeting/WaitingForParticipants';
import NoParticipantsWarning from './meeting/NoParticipantsWarning';
import ParticipantCardContent from './meeting/ParticipantCardContent';
import { useMeetingContinuity } from '../hooks/useMeetingContinuity';
import { visibleFailures } from '../utils/verificationFailures';

const EMPTY_TIMEOUT_MS = 15000;
const NOTICE_TICK_MS = 5000;

/**
 * Sidebar component for displaying meeting participants.
 * Works for both Zoom and MS Teams platforms.
 *
 * @param {Object} props
 * @param {string} props.platform - Either "zoom" or "ms-teams"
 * @param {string} [props.meetingId] - Meeting ID for Zoom (required for zoom platform)
 * @param {Object} [props.appContext] - App context (used for localStorage key fallback)
 * @param {string} [props.linkUuid] - Link UUID for standalone sidebar (bypasses Teams SDK meeting ID)
 */
function Sidebar({ platform, meetingId: propMeetingId, appContext, linkUuid }) {
  const { t } = useTranslation('shared');
  const [verificationFailures, setVerificationFailures] = useState([]);
  const [dismissedFailureIds, setDismissedFailureIds] = useState(() => {
    const storageKey = propMeetingId || appContext?.meeting?.id || 'global';
    const stored = localStorage.getItem(`dismissed_failures_${storageKey}`);
    return stored ? JSON.parse(stored) : [];
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [meetingStatus, setMeetingStatus] = useState('loading');
  const [shouldPoll, setShouldPoll] = useState(true);
  const [meetingId, setMeetingId] = useState(propMeetingId);
  const [linkUrl, setLinkUrl] = useState(null);
  const [actualMeetingId, setActualMeetingId] = useState(null);
  const { api_client } = usePolyAuth();

  // For Teams, get meeting ID from app context (passed from App.jsx after SDK initialization)
  // When linkUuid is provided (standalone sidebar), skip waiting for Teams SDK context
  useEffect(() => {
    if (linkUuid) {
      // Standalone mode: linkUuid-based fetching doesn't need a meetingId
      return;
    }
    if (platform === 'ms-teams' && !meetingId) {
      if (appContext?.meeting?.id) {
        setMeetingId(appContext.meeting.id);
      } else if (appContext) {
        // appContext loaded but no meeting ID present
        setMeetingStatus('not_polyguard');
        setShouldPoll(false);
      }
      // If appContext is null, wait for it (effect re-runs when appContext changes)
    }
  }, [platform, meetingId, appContext, linkUuid]);

  // Get the platform-specific name field
  const getScreenName = (user) => {
    if (platform === 'zoom') {
      return user.zoom_name;
    }
    return user.teams_name;
  };

  const local_fetcher = async (fake_url) => {
    if (!meetingId && !linkUuid) {
      return [];
    }

    const url = linkUuid
      ? `/v2/meeting-by-link/${linkUuid}`
      : `/v2/meeting/${platform}/${meetingId}`;

    try {
      const data = (await api_client.get(url)).data;

      // If not a polyguard meeting, stop polling
      if (!data.is_polyguard_meeting) {
        setMeetingStatus('not_polyguard');
        setShouldPoll(false);
        return [];
      }

      setLinkUrl(data.link_url);
      if (data.meeting_id) {
        setActualMeetingId(data.meeting_id);
      }

      const parts = [];
      Object.keys(data.users || {}).forEach(function (userID) {
        const user = JSON.parse(data.users[userID]);
        const isVerified = user.certainty > 0;
        parts.push({
          zoomParticipant: {
            screenName: getScreenName(user),
            name: user.name,
            region: user.region,
            phone_number: user.phone_number,
            age: user.age,
          },
          pslUser: user,
          isVerified,
          status: user ? (user.unverified ? 'unverified' : (isVerified ? 'verified' : 'mismatch')) : 'unverified'
        });
      });

      if (data.verification_failures) {
        const failures = Object.keys(data.verification_failures)
          .map(failureId => {
            const failureData = data.verification_failures[failureId];
            return typeof failureData === 'string' ? JSON.parse(failureData) : failureData;
          }) ?? [];
        failures.sort((a, b) => b.failure_id.localeCompare(a.failure_id));
        setVerificationFailures(failures);
      }

      // Determine meeting status based on participants
      if (parts.length > 0) {
        setMeetingStatus('ready');
      } else {
        // No participants - set waiting (setTimeout handles transition to warning)
        setMeetingStatus(prev => prev === 'warning' ? 'warning' : 'waiting');
      }
      return parts;
    } catch (error) {
      console.error('Error fetching data:', error);
      // Keep polling on error - might recover
      return [];
    }
  };

  const swrKey = linkUuid
    ? (shouldPoll ? `meeting-link-${linkUuid}` : null)
    : (shouldPoll && meetingId ? `meeting-${platform}-${meetingId}` : null);
  const { data: pslUsers, error } = useSWR(
    swrKey,
    local_fetcher,
    { refreshInterval: 3000 }
  );

  const { continuity, accountName } = useMeetingContinuity(pslUsers, actualMeetingId);

  const dismissFailure = (failureId) => {
    const newDismissed = [...dismissedFailureIds, failureId];
    setDismissedFailureIds(newDismissed);
    try {
      const storageKey = meetingId || appContext?.meeting?.id || 'global';
      localStorage.setItem(`dismissed_failures_${storageKey}`, JSON.stringify(newDismissed));
    } catch (e) {
      console.error('Failed to save dismissed failures:', e);
    }
  };

  const filteredFailures = useMemo(
    () => visibleFailures(verificationFailures, dismissedFailureIds, nowMs),
    [verificationFailures, dismissedFailureIds, nowMs]
  );

  useEffect(() => {
    if (!error) return;
    toast(error?.info ?? String(error));
  }, [error]);

  useEffect(() => {
    const tickId = setInterval(() => setNowMs(Date.now()), NOTICE_TICK_MS);
    return () => clearInterval(tickId);
  }, []);

  // When entering waiting state, set a timeout to transition to warning
  useEffect(() => {
    if (meetingStatus !== 'waiting') return;

    const timeoutId = setTimeout(() => {
      setMeetingStatus('warning');
    }, EMPTY_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [meetingStatus]);

  return (
    <Box sx={{ p: 2.5, maxWidth: 1200, mx: 'auto' }}>
      <FailuresSection failures={filteredFailures} onDismiss={dismissFailure} />

      <Typography variant="h5" fontWeight="bold" mb={2.5}>
        {t('sidebar.meetingParticipants')}
      </Typography>


      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {meetingStatus === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {meetingStatus === 'not_polyguard' && <NotPolyguardMeeting />}
        {meetingStatus === 'waiting' && <WaitingForParticipants />}
        {meetingStatus === 'warning' && <NoParticipantsWarning />}
        {meetingStatus === 'ready' && pslUsers?.map((participant, index) => (
          <ParticipantCardContent
            key={index}
            participant={participant}
            continuityData={continuity ? continuity[participant.pslUser?.poly_user_id] : undefined}
            accountName={accountName}
          />
        ))}
      </Box>

      {linkUrl && (
        <>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={async () => {
              await fallbackCopyTextToClipboard(linkUrl);
              toast.success(t('linkList.linkCopied'));
            }}
            sx={{ mt: 2.5, textTransform: 'none', color: 'text.secondary' }}
          >
            {t('sidebar.linkToMeeting')}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
            {t('sidebar.invitationNote')}
          </Typography>
        </>
      )}
    </Box>
  );
}

export default Sidebar;
